import {Locale, useConfig} from '@aboutyou/config';
import React, {
  FC,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useUsedTranslations} from './UsedTranslationsProvider';
import {TranslationFunction, createTranslator} from './createTranslator';
import {fetchTranslations} from './fetchTranslations';
import {DateFormatLong} from './getLongDateFormatForLocale';
import {DateFormatShort} from './getShortDateFormatForLocale';
import type {Currency} from './useFormatCurrency';
// eslint-disable-next-line monorepo/no-internal-import
import {useIsBotModeEnabled} from '@aboutyou/data-fetcher/src/ServerRenderingMode';
// eslint-disable-next-line monorepo/no-internal-import
import {useHydrationDone} from '@aboutyou/lazy-imports';

/**
 * The `Date.now` on the server when the response was created. This value is reset as soon as the client hydrates and should therefore only be used as a fallback value on hydration.
 */
type ServerDateNow = number | undefined;
export type Translations = Record<string, string>;
export type StaticI18nConfig = {
  locale: Locale;
  translations: Partial<Translations>;
  currencyConfig: Currency;
  dateFormat: {
    short: DateFormatShort;
    long: DateFormatLong;
  };
  serverDateNow: ServerDateNow;
};

type StaticI18nProviderValue = {
  locale: StaticI18nConfig['locale'];
  currencyConfig: StaticI18nConfig['currencyConfig'];
  dateFormat: StaticI18nConfig['dateFormat'];
  t: TranslationFunction;
  translations: StaticI18nConfig['translations'];
};

const I18nConfigContext = createContext<StaticI18nProviderValue>(
  new Proxy<StaticI18nProviderValue>({} as StaticI18nProviderValue, {
    get() {
      throw new Error(
        'Cannot find value for I18nConfigContext. Did you add a I18nProvider in one of the parent components?',
      );
    },
  }),
);

interface I18nProviderProps extends StaticI18nConfig {
  translatorOptions?: {
    prefix: string;
    suffix: string;
  };
  children?: React.ReactNode;
  disableTranslationLoader?: boolean;
  loadTranslationsFromOriginHost?: boolean;
}
export const I18nProvider: FC<I18nProviderProps> = ({
  children,
  translations: serverTranslations,
  translatorOptions,
  serverDateNow,
  loadTranslationsFromOriginHost = false,
  disableTranslationLoader = false,
  ...rest
}) => {
  const {usedTranslations} = useUsedTranslations();

  const clientTranslations = useClientTranslations();

  const translations = useMemo(
    () => ({
      ...serverTranslations,
      ...clientTranslations,
    }),
    [serverTranslations, clientTranslations],
  );

  /**
   * We want to collect all the used keys only
   * during the server rendering, this way
   * we can pass only the used ones to the client
   * @param key
   */
  const addToUsedTranslations = (key: string, value: string) => {
    if (!process.browser) {
      usedTranslations[key] = value;
    }
  };

  const t = createTranslator(
    translations,
    translatorOptions,
    addToUsedTranslations,
  );

  const value = useMemo(
    () => ({
      t,
      translations,
      ...rest,
    }),
    [t, translations, rest],
  );

  return (
    <I18nConfigContext.Provider value={value}>
      {disableTranslationLoader ? null : (
        <TranslationLoader
          loadTranslationsFromOriginHost={loadTranslationsFromOriginHost}
        />
      )}
      <ServerDateNowProvider serverDateNow={serverDateNow}>
        {children}
      </ServerDateNowProvider>
    </I18nConfigContext.Provider>
  );
};

const TranslationLoader: React.FC<{
  loadTranslationsFromOriginHost?: boolean;
}> = ({loadTranslationsFromOriginHost}) => {
  const hostname = useConfig().hostname;

  const isBotMode = useIsBotModeEnabled();
  /**
   * Previously, in every test where we had the
   * FeatureProviderDev, the providers did not add
   * elements to the dom, with this loader component
   * that changed. This is why we are adding a check
   * for the environment, so that the tests, which use
   * the FeatureProviderDev and expect the screen to be
   * empty dont fail
   *
   * If it is a bot, we don't need to fetch translations
   * as the bot requests static config from the server
   * which already includes all translations
   */
  if (process.env.NODE_ENV === 'test' || isBotMode) {
    return null;
  }
  /**
   * This injected script starts the fetching of the translations
   * from the middleware before even hydration starts. This minimizes the potentials
   * for flickering that could happen on client only components, since we don't transport
   * translations for the client only components in the initial html now.
   * NOTE: There is no guarantee that there will be no flickering on client
   * only components. Example: the network request to get translations from middleware is too slow.
   */
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `(${fetchTranslations.toString()})(${
          loadTranslationsFromOriginHost ? `'${hostname}'` : 'undefined'
        })`,
      }}
    />
  );
};

const ServerDateNowContext = createContext<ServerDateNow>(undefined);

// This is a stand alone provider as it will trigger a re-render on mount which should not affect re-renderings of other hooks exported from this context.
const ServerDateNowProvider: FC<
  React.PropsWithChildren<{serverDateNow: ServerDateNow}>
> = ({children, serverDateNow}) => {
  const [currentServerDateNow, setServerDateNow] = useState(serverDateNow);
  const isHydrationDone = useHydrationDone();

  useEffect(() => {
    if (!isHydrationDone) return;

    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setServerDateNow(undefined);
  }, [isHydrationDone]);

  return (
    <ServerDateNowContext.Provider value={currentServerDateNow}>
      {children}
    </ServerDateNowContext.Provider>
  );
};

const TRANSLATIONS_UPDATED_EVENT = 'translationsUpdated';

const useClientTranslations = () => {
  const [clientTranslations, setClientTranslations] = useState({});
  const isHydrationDone = useHydrationDone();

  const handleTranslationsUpdate = (event: Event) => {
    const data = (event as CustomEvent).detail;
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setClientTranslations(data);
  };
  /**
   * We are using this custom event listener
   * to update the translations on the client
   * once the translations are fetched
   */
  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    if (!isHydrationDone) return;

    // If the translations are already available on the window object
    // we can use them directly and do not need to listen for the event
    if (window.__CLIENT_TRANSLATIONS__) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect

      setClientTranslations(window.__CLIENT_TRANSLATIONS__);
      return;
    }

    document.addEventListener(
      TRANSLATIONS_UPDATED_EVENT,
      handleTranslationsUpdate,
    );

    return () => {
      document.removeEventListener(
        TRANSLATIONS_UPDATED_EVENT,
        handleTranslationsUpdate,
      );
    };
  }, [isHydrationDone]);

  return clientTranslations;
};

export const useCurrencyConfig = () => {
  return useContext(I18nConfigContext).currencyConfig;
};

export const useTranslation = () => {
  const {t} = useContext(I18nConfigContext);
  return {t};
};

export const useLocale = () => {
  return useContext(I18nConfigContext).locale;
};

export const useDateFormat = () => {
  return useContext(I18nConfigContext).dateFormat;
};

export const useServerDateNow = () => {
  return useContext(ServerDateNowContext);
};
